import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import {
  Wizard,
  WizardHeader,
  WizardContent,
} from "@Shared/Components/Layouts/Wizard/Wizard";

import { Box } from "@UI/Components/Box/Box";
import { Button } from "@UI/Components/Button/Button";
import { Container } from "@UI/Components/Container/Container";
import { Heading } from "@UI/Components/Heading/Heading";
import { Text } from "@UI/Components/Text/Text";
import { HStack, VStack } from "@UI/Components/Stack/Stack";
import { PhoneInput } from "@UI/Components/PhoneInput/PhoneInput";
import { Spinner } from "@UI/Components/Spinner/Spinner";

import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import useI18n from "@I18n/hooks/useI18n";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import { useSearchParams } from "@Libs/Router/hooks/useSearchParams";
import {
  sendVerificationCode,
  validatePhone,
} from "../services/phoneVerification";

import messages from "../messages";

const RESEND_COOLDOWN_KEY = "rt.phoneVerification.resendCooldownExpiry";
const SMS_SENT_KEY = "rt.phoneVerification.smsSent";
const LAST_SENT_PHONE_KEY = "rt.phoneVerification.lastSentPhone";
const RESEND_COOLDOWN_S = 60;

const COUNTRY_ID_TO_ISO: Record<number, string> = {
  1: "be",
  2: "fr",
};

const CONTACT_URL_BY_LOCALE: Record<string, string> = {
  "fr-BE": "https://ringtwice.be/fr/contact?contact-form=true",
  "nl-BE": "https://ringtwice.be/nl/contacteer-ons?contact-form=true",
  "fr-FR": "https://ringtwice.fr/contact?contact-form=true",
};

const SMS_ERROR_MESSAGES: Record<string, keyof typeof messages> = {
  banned_phone_number: "verification.phoneNumberVerification.phone_not_allowed",
  invalid_phone_number: "verification.phoneNumberVerification.invalid_phone_number",
  sms_not_a_mobile_number: "verification.phoneNumberVerification.sms_not_a_mobile_number",
  sms_delivery_failed: "verification.phoneNumberVerification.sms_delivery_failed",
};

interface PhoneFormValues {
  phoneNumber: string;
}

export const PhoneNumberInputStep: React.FC = () => {
  const { formatMessage, locale } = useI18n();
  const contactUrl = CONTACT_URL_BY_LOCALE[locale] ?? CONTACT_URL_BY_LOCALE["fr-BE"];
  const { currentUser, isLoading, isSuccess } = useCurrentUser({
    includes: ["serviceProvider"],
  });
  const navigate = useNavigate();
  const rawCallbackUrl = useSearchParams("callbackUrl");
  const callbackUrl = rawCallbackUrl
    ? rawCallbackUrl.startsWith("/") ? rawCallbackUrl : `/${rawCallbackUrl}`
    : null;

  const initialCountry =
    COUNTRY_ID_TO_ISO[currentUser?.countryId ?? 0] ?? "be";

  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMaxAttempts, setIsMaxAttempts] = useState(false);
  const [contactSupportErrorKey, setContactSupportErrorKey] = useState<keyof typeof messages | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { errors },
  } = useForm<PhoneFormValues>({
    defaultValues: { phoneNumber: "" },
  });

  const currentPhoneValue = watch("phoneNumber");

  useEffect(() => {
    if (isSuccess) {
      reset({ phoneNumber: currentUser?.tel ?? "" });
      setIsPhoneValid(!!currentUser?.tel);
    }
  }, [isSuccess]);

  useEffect(() => {
    setContactSupportErrorKey(null);
    setIsMaxAttempts(false);
  }, [currentPhoneValue]);

  const onSubmit = async ({ phoneNumber }: PhoneFormValues) => {
    if (!isPhoneValid) {
      setError("phoneNumber", {
        message: formatMessage(messages["verification.phoneNumberVerification.invalid_phone_number"]),
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const { data } = await validatePhone(phoneNumber);
      if (!data.valid) {
        setError("phoneNumber", {
          message: "This phone number is not allowed.",
        });
        return;
      }

      const stored = sessionStorage.getItem(RESEND_COOLDOWN_KEY);
      const lastPhone = sessionStorage.getItem(LAST_SENT_PHONE_KEY);
      const cooldownActive = stored ? Number(stored) - Date.now() > 0 : false;

      if (cooldownActive && lastPhone === phoneNumber) {
        navigate("PHONE_NUMBER_VERIFICATION", {
          params: { step: "verify" },
          ...(callbackUrl && { queryParams: { callbackUrl } }),
        });
        return;
      }

      await sendVerificationCode(phoneNumber);
      sessionStorage.setItem(SMS_SENT_KEY, "1");
      sessionStorage.setItem(RESEND_COOLDOWN_KEY, String(Date.now() + RESEND_COOLDOWN_S * 1000));
      sessionStorage.setItem(LAST_SENT_PHONE_KEY, phoneNumber);
      navigate("PHONE_NUMBER_VERIFICATION", {
        params: { step: "verify" },
        ...(callbackUrl && { queryParams: { callbackUrl } }),
      });
    } catch (error: unknown) {
      const errorKey = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
      if (errorKey === "too_many_retries") {
        setIsMaxAttempts(true);
      } else if (errorKey === "sms_region_not_supported" || errorKey === "sms_opted_out") {
        setContactSupportErrorKey(`verification.phoneNumberVerification.${errorKey}`);
      } else {
        const messageKey = (errorKey && SMS_ERROR_MESSAGES[errorKey]) || "verification.phoneNumberVerification.sms_delivery_failed";
        setError("phoneNumber", { message: formatMessage(messages[messageKey]) });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Wizard
      paddingTop={{ base: 5, md: 8 }}
      maxW={{
        base: "container.sm",
        sm: "container.sm",
        lg: "container.sm",
      }}
    >
      <WizardHeader marginBottom={4}>
        <Container textAlign="start" px={{ base: 0 }}>
          <VStack alignItems="start" spacing={2}>
            <Heading fontSize={{ base: "3xl", lg: "5xl" }}>
              <FormattedMessage
                id={"verification.phoneNumberVerification.title"}
                defaultMessage={"Verify Your Phone Number"}
              />
            </Heading>
            <Text>
              <FormattedMessage
                id={
                  "verification.phoneNumberVerification.phone_input_description"
                }
                defaultMessage={
                  "Your number remains private on Ring Twice. The client will get it once they've chosen you for the job."
                }
              />
            </Text>
          </VStack>
        </Container>
      </WizardHeader>
      <WizardContent>
        <Box
          as="form"
          onSubmit={handleSubmit(onSubmit)}
          borderRadius="lg"
          padding={8}
          bg="white"
        >
          <VStack spacing={6} alignItems="stretch">
            <VStack spacing={1} alignItems="stretch">
              <Box maxWidth="400px" alignSelf="center" width="100%">
              <Controller
                name="phoneNumber"
                control={control}
                render={({ field }) => (
                  <PhoneInput
                    id="phone-number"
                    label={formatMessage(
                      messages["verification.phoneNumberVerification.phone_number_label"]
                    )}
                    initialValue={currentUser?.tel ?? ""}
                    initialCountry={initialCountry}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    onValidityChange={setIsPhoneValid}
                    isError={!!errors.phoneNumber}
                    errorMessage={errors.phoneNumber?.message}
                    disabled={isLoading}
                    locale={locale}
                  />
                )}
              />
              </Box>
              {contactSupportErrorKey && (
                <Text fontSize="sm" color="red.500" textAlign="left" maxW="400px" alignSelf="center" mb={0}>
                  <FormattedMessage id={contactSupportErrorKey} />{" "}
                  <Box as="a" href={contactUrl} target="_blank" rel="noopener noreferrer" color="pink.400" textDecoration="underline" display="inline">
                    <FormattedMessage
                      id={"verification.phoneNumberVerification.contact_support"}
                      defaultMessage={"Please contact support"}
                    />
                  </Box>
                </Text>
              )}
            </VStack>
            {isMaxAttempts && (
              <HStack justifyContent="center" flexWrap="wrap" spacing={1} maxW="400px" alignSelf="center">
                <Text fontSize="sm" color="gray.700" mb={0}>
                  <FormattedMessage
                    id={"verification.phoneNumberVerification.max_attempts_message"}
                    defaultMessage={"You have reached your maximum number of attempts."}
                  />
                </Text>
                <Box as="a" href={contactUrl} target="_blank" rel="noopener noreferrer" color="pink.400" textDecoration="underline" fontSize="sm">
                  <FormattedMessage
                    id={"verification.phoneNumberVerification.contact_support"}
                    defaultMessage={"Please contact support"}
                  />
                </Box>
              </HStack>
            )}
            <HStack justifyContent="center" spacing={3}>
              <Button
                type="button"
                colorScheme="gray"
                size="lg"
                onClick={() => { window.location.href = callbackUrl ?? "/dashboard"; }}
              >
                <FormattedMessage id="cancel" defaultMessage="Cancel" />
              </Button>
              <Button
                type="submit"
                colorScheme="seaGreen"
                size="lg"
                maxW="300px"
                position="relative"
                isDisabled={
                  isSubmitting ||
                  isLoading ||
                  (currentUser?.serviceProvider?.phoneNumberValidated === true &&
                    currentPhoneValue === currentUser?.tel)
                }
              >
                <Box as="span" opacity={isSubmitting ? 0 : 1}>
                  <FormattedMessage
                    id={"verification.phoneNumberVerification.verify_number"}
                    defaultMessage={"Verify my number"}
                  />
                </Box>
                {isSubmitting && <Spinner size="sm" position="absolute" />}
              </Button>
            </HStack>
          </VStack>
        </Box>
      </WizardContent>
    </Wizard>
  );
};
